import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Suspense } from "react";
import { PageLoader } from "@/components/PageLoader";
import { useState } from "react";
import {
    FileText, CalendarDays, LogOut, Menu, X, Stethoscope,
    Briefcase, Clock, ChevronDown, ShieldCheck, MessageSquare, HeartPulse, CalendarClock, ShieldAlert, Pill, CalendarOff, ClipboardList, FileSignature, Accessibility, Utensils,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import { StaffConsentGate } from "@/components/StaffConsentGate";
import { ProviderOnboardingGate } from "@/components/ProviderOnboardingGate";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { usePreReleaseUpdates } from "@/hooks/usePreReleaseUpdates";
import { useRefillRequests } from "@/hooks/useRefillRequests";
import { useSickCallReviews } from "@/hooks/useSickCallReviews";
import { useVerbalOrders } from "@/hooks/useVerbalOrders";
import { useDietOrders } from "@/hooks/useDietOrders";
import { useMedicaidAlerts } from "@/hooks/useMedicaidAlerts";
import { useStandingOrdersReview } from "@/hooks/useStandingOrdersReview";
import { useAdaReferrals } from "@/hooks/useAdaReferrals";
import { ProviderNotificationBell } from "@/components/ProviderNotificationBell";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const navItems = [
    { to: "/provider", icon: CalendarDays, label: "Schedule", tid: "prov-nav-dashboard", exact: true, badge: "refills" },
    { to: "/provider/clinical-records", icon: FileText, label: "Clinical Records", tid: "prov-nav-clinical-records", clinicalDoc: true },
    { to: "/provider/ada", icon: Accessibility, label: "ADA Accommodations", tid: "prov-nav-ada", badge: "ada", clinicalOnly: true },
    { to: "/provider/sick-call-reviews", icon: Stethoscope, label: "Sick Call Reviews", tid: "prov-nav-sick-call-reviews", badge: "sickcall", clinicalDoc: true },
    { to: "/provider/verbal-orders", icon: Pill, label: "Verbal Orders", tid: "prov-nav-verbal-orders", badge: "verbal", clinicalDoc: true },
    { to: "/provider/diet-orders", icon: Utensils, label: "Diet Orders", tid: "prov-nav-diet-orders", badge: "diet", medicalOnly: true },
    { to: "/provider/watch", icon: ShieldAlert, label: "Active Precautions", tid: "prov-nav-watch", clinicalDoc: true },
    { to: "/provider/pre-release", icon: CalendarClock, label: "Pre-Release", tid: "prov-nav-prerelease", badge: "prerelease" },
    { to: "/provider/mat", icon: Pill, label: "MAT / MOUD", tid: "prov-nav-mat", matOnly: true },
    { to: "/provider/medicaid", icon: HeartPulse, label: "Medicaid Tracker", tid: "prov-nav-medicaid", therapistOnly: true, badge: "medicaid" },
    { to: "/provider/caseload", icon: ClipboardList, label: "My Caseload", tid: "prov-nav-caseload", therapistOnly: true },
    { to: "/provider/standing-orders", icon: ClipboardList, label: "Standing Orders", tid: "prov-nav-standing-orders", mdOnly: true, badge: "standing_orders" },
    { to: "/provider/compliance-attestations", icon: FileSignature, label: "Compliance Attestations", tid: "prov-nav-attestations", mdOnly: true, badge: "attestations" },
    { to: "/provider/messages", icon: MessageSquare, label: "Messages", tid: "prov-nav-messages", badge: "messages" },
    { to: "/provider/coverage", icon: CalendarOff, label: "Coverage & Time Away", tid: "prov-nav-coverage", byoOnly: true },
];

export default function ProviderLayout() {
    const { user, logout, fetchMe } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const unread = useUnreadMessages();
    const prereleaseCount = usePreReleaseUpdates();
    const refillCount = useRefillRequests();
    const sickCallCount = useSickCallReviews();
    const verbalOrdersCount = useVerbalOrders();

    // Medicaid Tracker is a therapist-only responsibility — hidden for psych/medical
    // providers, and also hidden unless ≥1 of the provider's facilities has the add-on.
    const isTherapist = user?.care_role === "therapist";
    const isMedical = user?.care_role === "medical";
    const dietOrdersCount = useDietOrders(isMedical);
    const adaReferralCount = useAdaReferrals(!isTherapist);
    const medicaidEnabled = !!user?.medicaid_addon;
    const medicaidCount = useMedicaidAlerts(isTherapist && medicaidEnabled);
    const standingOrdersReview = useStandingOrdersReview("/provider/standing-orders/review-count", !!user?.is_medical_director);
    const attestationsCount = useStandingOrdersReview("/provider/compliance-attestations/count", !!user?.is_medical_director);
    // BYO (facility-employed) providers: no platform HR/payroll — hide Employment & Time & Pay,
    // and instead use Coverage & Time Away (talks to the facility's coverage board).
    const isByo = user?.employment_type === "facility";
    // Blue Phoenix Medical Directors (non-facility MDs) manage & e-sign the master protocol templates.
    const isBpMd = !!user?.is_medical_director && user?.employment_type !== "facility";
    // Jail case managers (BYO, non-clinical) get Medicaid + Pre-Release only — no clinical documentation surfaces.
    const isCaseManager = !!user?.case_manager;
    const items = navItems.filter((it) => (!it.therapistOnly || (isTherapist && medicaidEnabled)) && (!it.matOnly || user?.mat_provider) && (!it.mdOnly || user?.is_medical_director) && (!it.medicalOnly || isMedical) && (!it.prescriberOnly || !isTherapist) && (!it.byoOnly || isByo) && (!it.clinicalOnly || !isTherapist) && (!it.clinicalDoc || !isCaseManager) && (!isCaseManager || ["Pre-Release", "Medicaid Tracker", "My Caseload"].includes(it.label)));

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    // Invited providers must finish the onboarding progression gate first
    // (Application → Contract → W-9 → Consents → submit), locked until admin approval.
    if (user && user.onboarding_status && user.onboarding_status !== "active") {
        return <ProviderOnboardingGate />;
    }

    // All care providers must change a one-time password and sign onboarding consents.
    if (user && (user.must_change_password || !user.staff_consent_accepted)) {
        return <StaffConsentGate basePath="/provider" needsPassword={!!user.must_change_password} onDone={fetchMe} />;
    }

    return (
        <div className="min-h-screen flex bg-phoenix-bg">
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-40 bg-black/40"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <aside
                data-testid="provider-sidebar"
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-white border-r border-border/70 flex flex-col transform transition-transform duration-200 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
            >
                <div className="px-5 py-5 border-b border-border/70 flex items-center justify-between">
                    <PhoenixWordmark subtitle />
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden text-muted-foreground hover:text-phoenix-navy p-1"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-5 py-3 border-b border-border/70 bg-phoenix-soft/40">
                    <div data-testid="provider-portal-label" className="flex items-center gap-2 text-xs font-semibold text-phoenix-navy uppercase tracking-wider">
                        {isTherapist ? <ClipboardList className="w-3.5 h-3.5" /> : <Stethoscope className="w-3.5 h-3.5" />} {isTherapist ? "Case Manager Portal" : "Clinical Portal"}
                    </div>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                    {items.map(({ to, icon: Icon, label, tid, exact, badge }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={exact}
                            data-testid={tid}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-phoenix-navy text-white"
                                        : "text-muted-foreground hover:bg-secondary/60 hover:text-phoenix-navy"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            <span className="flex-1">{label}</span>
                            {badge === "messages" && unread > 0 && (
                                <span data-testid="prov-messages-badge" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-phoenix-orange text-white text-[11px] font-bold">{unread}</span>
                            )}
                            {badge === "prerelease" && prereleaseCount > 0 && (
                                <span data-testid="prov-prerelease-badge" title="Unreviewed facility pre-release updates" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{prereleaseCount}</span>
                            )}
                            {badge === "refills" && refillCount > 0 && (
                                <span data-testid="prov-refills-badge" title="Pending refill requests" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{refillCount}</span>
                            )}
                            {badge === "sickcall" && sickCallCount > 0 && (
                                <span data-testid="prov-sickcall-badge" title="Kites flagged for expedited review" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{sickCallCount}</span>
                            )}
                            {badge === "verbal" && verbalOrdersCount > 0 && (
                                <span data-testid="prov-verbal-badge" title="Verbal orders awaiting co-signature" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{verbalOrdersCount}</span>
                            )}
                            {badge === "diet" && dietOrdersCount > 0 && (
                                <span data-testid="prov-diet-badge" title="Diet requests / verbal orders / renewals awaiting your action" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{dietOrdersCount}</span>
                            )}
                            {badge === "medicaid" && medicaidCount > 0 && (
                                <span data-testid="prov-medicaid-badge" title="Aging / past-due Medicaid applications" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{medicaidCount}</span>
                            )}
                            {badge === "standing_orders" && standingOrdersReview > 0 && (
                                <span data-testid="prov-standing-orders-badge" title="Standing orders overdue or due within 30 days for annual review" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[11px] font-bold">{standingOrdersReview}</span>
                            )}
                            {badge === "attestations" && attestationsCount > 0 && (
                                <span data-testid="prov-attestations-badge" title="compliance artifacts awaiting your signature" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{attestationsCount}</span>
                            )}
                            {badge === "ada" && adaReferralCount > 0 && (
                                <span data-testid="prov-ada-badge" title="ADA accommodation referrals awaiting your clinical order" className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">{adaReferralCount}</span>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            <main className="flex-1 flex flex-col min-w-0">
                {/* Top header — mobile menu toggle (left) + user menu with sign out (upper right) */}
                <header
                    data-testid="provider-topbar"
                    className="sticky top-0 z-30 bg-white border-b border-border/70 px-4 sm:px-6 py-3 flex items-center gap-3"
                >
                    <button
                        onClick={() => setSidebarOpen(true)}
                        data-testid="provider-sidebar-toggle"
                        className="lg:hidden p-2 -ml-2 rounded-lg text-phoenix-navy hover:bg-secondary/60 transition-colors"
                        aria-label="Open menu"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <PhoenixWordmark size={28} className="lg:hidden" />

                    <div className="ml-auto flex items-center gap-2 sm:gap-3">
                        <ProviderNotificationBell />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="provider-user-menu"
                                    className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-secondary/60 transition-colors max-w-[240px]"
                                >
                                    <span className="w-8 h-8 rounded-full bg-phoenix-navy text-white flex items-center justify-center shrink-0">
                                        <Stethoscope className="w-4 h-4" />
                                    </span>
                                    <div className="text-right leading-tight hidden sm:block min-w-0">
                                        <div className="text-sm font-semibold text-foreground truncate">
                                            {user?.full_name || user?.email?.split("@")[0]}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                                <div className="px-2 py-1.5 sm:hidden">
                                    <div className="text-sm font-semibold text-foreground truncate">
                                        {user?.full_name || user?.email?.split("@")[0]}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                </div>
                                <DropdownMenuSeparator className="sm:hidden" />
                                {isBpMd && (
                                    <DropdownMenuItem
                                        data-testid="provider-menu-master-protocols"
                                        onSelect={() => navigate("/provider/master-protocols")}
                                        className="gap-2 cursor-pointer"
                                    >
                                        <ShieldCheck className="w-4 h-4" /> Protocol Templates
                                    </DropdownMenuItem>
                                )}
                                {!isByo && (
                                  <>
                                    <DropdownMenuItem
                                        data-testid="provider-menu-employment"
                                        onSelect={() => navigate("/provider/employment")}
                                        className="gap-2 cursor-pointer"
                                    >
                                        <Briefcase className="w-4 h-4" /> Employment
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="provider-menu-time"
                                        onSelect={() => navigate("/provider/time")}
                                        className="gap-2 cursor-pointer"
                                    >
                                        <Clock className="w-4 h-4" /> Time &amp; Pay
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem
                                    data-testid="provider-logout"
                                    onSelect={handleLogout}
                                    className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                                >
                                    <LogOut className="w-4 h-4" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>
                <div className="flex-1 overflow-auto">
                    <Suspense fallback={<PageLoader />}>
                        <Outlet />
                    </Suspense>
                </div>
            </main>
        </div>
    );
}
